/* eslint-disable eag/match-named-export */
import {Module} from '@dms/api/shared';
import {moduleKeys} from '@dms/config';
import {featureFlags} from '@dms/feature-flags';
import {vehiclesRoutes} from '@dms/routes';
import {permissions, ServiceVehicleFormMode, ServiceVehicleFormVariant} from '@dms/shared';
import {EditBaseVehicle} from '@dms/vehicle-forms/base';
import {
  CreateRentalForVehicle,
  CreateRentalVehicle,
  EditRentalVehicle as EditRentalVehicleV2,
} from '@dms/vehicle-forms/rental';
import {
  CloneSaleVehicle,
  CreateSaleVehicle,
  CreateSaleVehicleFromExisting,
} from '@dms/vehicle-forms/sale';
import {
  CloneServiceVehicle,
  CreateServiceFromSaleVehicle,
  CreateServiceVehicle,
  EditServiceIntervals,
} from '@dms/vehicle-forms/service';

import {EditOrCloneServiceVehicle} from './pages/EditOrCloneServiceVehicle/EditOrCloneServiceVehicle';
import {EditRentalVehicle} from './pages/EditRentalVehicle/EditRentalVehicle';
import {ExternalVehicleRedirect} from './pages/ExternalVehicleRedirect/ExternalVehicleRedirect';
import {NewRentalVehicle} from './pages/NewRentalVehicle/NewRentalVehicle';
import {NewRentalVehicleFromExistingVehicle} from './pages/NewRentalVehicleFromExistingVehicle/NewRentalVehicleFromExistingVehicle';
import {NewServiceVehicle} from './pages/NewServiceVehicle/NewServiceVehicle';
import {NewServiceVehicleFromSale} from './pages/NewServiceVehicleFromSale/NewServiceVehicleFromSale';
import {NewVehicleShell} from './pages/NewVehicleShell/NewVehicleShell';
import {VehicleDetail} from './pages/VehicleDetail/VehicleDetail';
import {newVehicleConfiguratorAccess} from './pages/Vehicles/newVehicleAccess';
import {Vehicles} from './pages/Vehicles/Vehicles';
import {WarehouseMovements} from './pages/WarehouseMovements/WarehouseMovements';

export const VehiclesModule: Module = {
  id: moduleKeys.vehicles,
  requiredPermission: permissions.vehicleRead,
  layoutProps: {
    title: 'page.vehicles.title',
    icon: 'sidebar/vehicles',
  },
  routerProps: {
    path: vehiclesRoutes.home,
    element: <Vehicles />,
  },
  content: [
    {
      id: 'vehicles.home',
      routerProps: {
        path: vehiclesRoutes.home,
        element: <Vehicles />,
      },
      layoutProps: {
        title: 'page.vehicles.title',
        icon: 'sidebar/vehicles',
      },
    },
    {
      id: 'vehicles.newVehicleConfigurations',
      requiredFeatureFlag: featureFlags.NEW_VEHICLE_CATALOGUE,
      requiredPermission: permissions.newVehicleConfigurationRead,
      routerProps: {
        path: vehiclesRoutes.newVehicleConfigurations,
        element: <Vehicles />,
      },
    },
    {
      id: 'vehicles.newVehicleCatalogue',
      requiredFeatureFlag: featureFlags.NEW_VEHICLE_CATALOGUE,
      requiredPermission: permissions.newVehicleCatalogueRead,
      routerProps: {
        path: vehiclesRoutes.newVehicleCatalogue,
        element: <NewVehicleShell />,
      },
    },
    {
      id: 'vehicles.newVehicleCatalogue.newModel',
      requiredFeatureFlag: featureFlags.NEW_VEHICLE_CATALOGUE,
      requiredPermission: permissions.newVehicleCatalogueWrite,
      routerProps: {
        path: vehiclesRoutes.newVehicleCatalogueNewModel,
        element: <NewVehicleShell />,
      },
    },
    {
      id: 'vehicles.newVehicleCatalogue.make',
      requiredFeatureFlag: featureFlags.NEW_VEHICLE_CATALOGUE,
      requiredPermission: permissions.newVehicleCatalogueRead,
      routerProps: {
        path: vehiclesRoutes.newVehicleCatalogueMake,
        element: <NewVehicleShell />,
      },
    },
    {
      id: 'vehicles.newVehicleCatalogue.model',
      requiredFeatureFlag: featureFlags.NEW_VEHICLE_CATALOGUE,
      requiredPermission: permissions.newVehicleCatalogueRead,
      routerProps: {
        path: vehiclesRoutes.newVehicleCatalogueModel,
        element: <NewVehicleShell />,
      },
    },
    {
      id: 'vehicles.newVehicleCatalogue.modelYear',
      requiredFeatureFlag: featureFlags.NEW_VEHICLE_CATALOGUE,
      requiredPermission: permissions.newVehicleCatalogueRead,
      routerProps: {
        path: vehiclesRoutes.newVehicleCatalogueModelYear,
        element: <NewVehicleShell />,
      },
    },
    {
      id: 'vehicles.newVehicleCatalogue.preview',
      requiredFeatureFlag: featureFlags.NEW_VEHICLE_CATALOGUE,
      requiredPermissionAtLeastOne: [
        permissions.newVehicleCatalogueWrite,
        permissions.newVehicleCataloguePublish,
      ],
      routerProps: {
        path: vehiclesRoutes.newVehicleCatalogueModelYearPreview,
        element: <NewVehicleShell />,
      },
    },
    {
      id: 'vehicles.newVehicleConfigurator',
      ...newVehicleConfiguratorAccess,
      routerProps: {
        path: vehiclesRoutes.newVehicleConfigurator,
        element: <NewVehicleShell />,
      },
    },
    {
      id: 'vehicles.externalVehicleRedirect',
      routerProps: {
        path: vehiclesRoutes.externalVehicleRedirect,
        element: <ExternalVehicleRedirect />,
      },
    },
    {
      id: 'vehicles.inAdvertisement',
      requiredPermission: permissions.toggleTenantPlatform,
      routerProps: {
        path: vehiclesRoutes.inAdvertisement,
        element: <Vehicles />,
      },
    },
    {
      id: 'vehicles.vehicleWarehouses',
      requiredPermission: permissions.vehicleWarehouseRead,
      routerProps: {
        path: vehiclesRoutes.vehicleWarehouses,
        element: <Vehicles />,
      },
      requiredFeatureFlag: featureFlags.VEHICLE_WAREHOUSE,
    },
    {
      id: 'vehicles.vehicleOpenSearch',
      routerProps: {
        path: vehiclesRoutes.vehicleOpenSearch,
        element: <Vehicles />,
      },
    },
    {
      id: 'vehicles.rentalVehicleOverview',
      routerProps: {
        path: vehiclesRoutes.rentalVehicleOverview,
        element: <Vehicles />,
      },
    },
    {
      id: 'vehicles.saleVehicleOpenSearch',
      routerProps: {
        path: vehiclesRoutes.saleVehicleOpenSearch,
        element: <Vehicles />,
      },
    },
    {
      id: 'vehicles.serviceVehicleOpenSearch',
      routerProps: {
        path: vehiclesRoutes.serviceVehicleOpenSearch,
        element: <Vehicles />,
      },
    },
    {
      id: 'vehicles.warehouseMovements',
      requiredPermission: permissions.vehicleWarehouseRead,
      routerProps: {
        path: vehiclesRoutes.warehouseMovements,
        element: <WarehouseMovements />,
      },
    },
    {
      id: 'vehicles.create.v2',
      requiredPermission: [permissions.vehiclePutUpForSale, permissions.vehiclePutUpToBuy],
      routerProps: {
        path: vehiclesRoutes.createV2,
        element: <CreateSaleVehicle />,
      },
      layoutProps: {
        title: 'entity.vehicle.actions.createVehicle',
        isPartOfCreateMenuItem: true,
      },
    },
    {
      id: 'vehicles.create.v2.tab',
      requiredPermission: [permissions.vehiclePutUpForSale, permissions.vehiclePutUpToBuy],
      routerProps: {
        path: vehiclesRoutes.createV2Tab,
        element: <CreateSaleVehicle />,
      },
    },
    {
      id: 'vehicles.create-sale-vehicle-from-existing.v2',
      requiredPermission: [permissions.vehiclePutUpForSale, permissions.vehiclePutUpToBuy],
      routerProps: {
        path: vehiclesRoutes.createSaleVehicleFromExistingVehicleV2,
        element: <CreateSaleVehicleFromExisting />,
      },
    },
    {
      id: 'vehicles.add-service',
      routerProps: {
        path: vehiclesRoutes.createServiceVehicle,
        element: <NewServiceVehicle />,
      },
    },
    {
      id: 'vehicles.add-rental',
      requiredPermission: permissions.createRentalVehicle,
      routerProps: {
        path: vehiclesRoutes.createRentalVehicle,
        element: <NewRentalVehicle />,
      },
    },
    {
      id: 'vehicles.add.v2.rental',
      requiredPermission: permissions.createRentalVehicle,
      routerProps: {
        path: vehiclesRoutes.createRentalVehicleV2,
        element: <CreateRentalVehicle />,
      },
    },
    {
      id: 'vehicles.edit.v2.rental',
      requiredPermission: permissions.editRentalVehicle,
      routerProps: {
        path: vehiclesRoutes.editRentalVehicleV2,
        element: <EditRentalVehicleV2 />,
      },
    },
    {
      id: 'vehicles.edit-rental',
      requiredPermission: permissions.editRentalVehicle,
      routerProps: {
        path: vehiclesRoutes.editRentalVehicle,
        element: <EditRentalVehicle />,
      },
    },
    {
      id: 'vehicles.create.v2.service',
      requiredPermission: permissions.workshopRead,
      routerProps: {
        path: vehiclesRoutes.createServiceVehicleV2,
        element: <CreateServiceVehicle />,
      },
    },
    {
      id: 'vehicles.create.v2.service.tab',
      requiredPermission: permissions.workshopRead,
      routerProps: {
        path: vehiclesRoutes.createServiceVehicleV2Tab,
        element: <CreateServiceVehicle />,
      },
    },
    {
      id: 'vehicles.createVehicleFromSaleOrService',
      requiredPermission: permissions.createRentalVehicle,
      routerProps: {
        path: vehiclesRoutes.createRentalVehicleFromExistingVehicle,
        element: <NewRentalVehicleFromExistingVehicle />,
      },
    },
    {
      id: 'vehicles.createVehicleFromSaleOrService.v2',
      requiredPermission: permissions.createRentalVehicle,
      routerProps: {
        path: vehiclesRoutes.createRentalVehicleFromExistingVehicleV2,
        element: <CreateRentalForVehicle />,
      },
    },
    {
      id: 'vehicles.add-service-from-sale',
      routerProps: {
        path: vehiclesRoutes.createServiceVehicleFromSale,
        element: <NewServiceVehicleFromSale />,
      },
    },
    {
      id: 'vehicles.edit.v2',
      requiredPermission: permissions.updateVehicle,
      routerProps: {
        paths: [vehiclesRoutes.editV2, vehiclesRoutes.editV2Tab],
        element: <EditBaseVehicle />,
      },
    },
    {
      id: 'vehicles.edit-service',
      routerProps: {
        path: vehiclesRoutes.editServiceVehicle,
        element: (
          <EditOrCloneServiceVehicle
            mode={ServiceVehicleFormMode.EDIT}
            variant={ServiceVehicleFormVariant.EXTENDED}
          />
        ),
      },
    },
    {
      id: 'vehicles.edit-service-intervals',
      requiredPermission: [permissions.updateVehicle, permissions.workshopRead],
      routerProps: {
        path: vehiclesRoutes.editServiceIntervals,
        element: <EditServiceIntervals />,
      },
    },
    {
      id: 'vehicles.clone.v2.tab',
      requiredPermission: permissions.cloneSaleVehicle,
      routerProps: {
        path: vehiclesRoutes.cloneV2Tab,
        element: <CloneSaleVehicle />,
      },
    },
    {
      id: 'vehicles.clone.v2',
      routerProps: {
        path: vehiclesRoutes.cloneV2,
        element: <CloneSaleVehicle />,
      },
    },
    {
      id: 'vehicles.clone-service-vehicle.v2',
      requiredPermission: permissions.workshopRead,
      routerProps: {
        path: vehiclesRoutes.cloneServiceVehicleV2,
        element: <CloneServiceVehicle />,
      },
    },
    {
      id: 'vehicles.clone-service-vehicle.v2.tab',
      requiredPermission: permissions.workshopRead,
      routerProps: {
        path: vehiclesRoutes.cloneServiceVehicleTabV2,
        element: <CloneServiceVehicle />,
      },
    },
    {
      id: 'vehicles.create-service-from-sale-vehicle.v2',
      requiredPermission: permissions.workshopRead,
      routerProps: {
        path: vehiclesRoutes.createServiceFromSaleVehicleV2,
        element: <CreateServiceFromSaleVehicle />,
      },
    },
    {
      id: 'vehicles.create-service-from-sale-vehicle.v2.tab',
      requiredPermission: permissions.workshopRead,
      routerProps: {
        path: vehiclesRoutes.createServiceFromSaleVehicleV2Tab,
        element: <CreateServiceFromSaleVehicle />,
      },
    },
    {
      id: 'vehicles.details',
      routerProps: {
        path: vehiclesRoutes.detail,
        element: <VehicleDetail />,
      },
    },
    {
      id: 'vehicles.newVehicleDetail',
      requiredFeatureFlag: featureFlags.NEW_VEHICLE_CATALOGUE,
      requiredPermission: permissions.newVehicleConfigurationRead,
      routerProps: {
        path: vehiclesRoutes.newVehicleDetail,
        element: <VehicleDetail />,
      },
    },
    {
      id: 'vehicles.newVehicleConfiguration',
      requiredFeatureFlag: featureFlags.NEW_VEHICLE_CATALOGUE,
      requiredPermission: permissions.newVehicleConfigurationRead,
      routerProps: {
        path: vehiclesRoutes.newVehicleConfiguration,
        element: <VehicleDetail />,
      },
    },
    {
      id: 'vehicles.newVehiclePriceBasis',
      requiredFeatureFlag: featureFlags.NEW_VEHICLE_CATALOGUE,
      requiredPermission: permissions.newVehicleConfigurationRead,
      routerProps: {
        path: vehiclesRoutes.newVehiclePriceBasis,
        element: <VehicleDetail />,
      },
    },
    {
      id: 'vehicles.newVehicleCustomFields',
      requiredFeatureFlag: featureFlags.NEW_VEHICLE_CATALOGUE,
      requiredPermission: permissions.newVehicleConfigurationRead,
      routerProps: {
        path: vehiclesRoutes.newVehicleCustomFields,
        element: <VehicleDetail />,
      },
    },
    {
      id: 'vehicles.vehicleData',
      routerProps: {
        path: vehiclesRoutes.vehicleData,
        element: <VehicleDetail />,
      },
    },
    {
      id: 'vehicles.features',
      routerProps: {
        path: vehiclesRoutes.features,
        element: <VehicleDetail />,
      },
    },
    {
      id: 'vehicles.financesRentalPrices',
      routerProps: {
        path: vehiclesRoutes.financesRentalPrices,
        element: <VehicleDetail />,
      },
    },
    {
      id: 'vehicles.serviceIntervals',
      routerProps: {
        path: vehiclesRoutes.serviceIntervals,
        element: <VehicleDetail />,
      },
    },
    {
      id: 'vehicles.additionalInformation',
      routerProps: {
        path: vehiclesRoutes.additionalInformation,
        element: <VehicleDetail />,
      },
    },
    {
      id: 'vehicles.rental',
      routerProps: {
        path: vehiclesRoutes.rental,
        element: <VehicleDetail />,
      },
    },
    {
      id: 'vehicles.insights',
      routerProps: {
        path: vehiclesRoutes.insights,
        element: <VehicleDetail />,
      },
    },
    {
      id: 'vehicles.conditionsInspections',
      routerProps: {
        path: vehiclesRoutes.conditionsInspections,
        element: <VehicleDetail />,
      },
    },
    {
      id: 'vehicles.inspectionDetail',
      routerProps: {
        path: vehiclesRoutes.inspectionDetail,
        element: <VehicleDetail />,
      },
    },
    {
      id: 'vehicles.inspectionDetail',
      routerProps: {
        path: vehiclesRoutes.inspectionDetailTab,
        element: <VehicleDetail />,
      },
    },
    {
      id: 'vehicles.conditions',
      routerProps: {
        path: vehiclesRoutes.conditions,
        element: <VehicleDetail />,
      },
    },
    {
      id: 'vehicles.conditionDetail',
      routerProps: {
        path: vehiclesRoutes.conditionDetail,
        element: <VehicleDetail />,
      },
    },
    {
      id: 'vehicles.conditionDetailTab',
      routerProps: {
        path: vehiclesRoutes.conditionDetailTab,
        element: <VehicleDetail />,
      },
    },
    {
      id: 'vehicles.photos',
      routerProps: {
        path: vehiclesRoutes.photos,
        element: <VehicleDetail />,
      },
    },
    {
      id: 'vehicles.photos',
      routerProps: {
        path: vehiclesRoutes.photoByInspectionId,
        element: <VehicleDetail />,
      },
    },
    {
      id: 'vehicles.publishing',
      routerProps: {
        path: vehiclesRoutes.publishing,
        element: <VehicleDetail />,
      },
    },
    {
      id: 'vehicles.auction',
      requiredFeatureFlag: featureFlags.AUCTIONS,
      routerProps: {
        path: vehiclesRoutes.auction,
        element: <VehicleDetail />,
      },
    },
    {
      id: 'vehicles.campaigns',
      requiredFeatureFlag: featureFlags.CAMPAIGNS,
      routerProps: {
        path: vehiclesRoutes.campaigns,
        element: <VehicleDetail />,
      },
    },
    {
      id: 'vehicles.businessCases',
      routerProps: {
        path: vehiclesRoutes.businessCases,
        element: <VehicleDetail />,
      },
    },
    {
      id: `${moduleKeys.vehicles}.complaints`,
      routerProps: {
        path: vehiclesRoutes.complaints,
        element: <VehicleDetail />,
      },
      requiredPermission: permissions.complaintRead,
    },
    {
      id: `${moduleKeys.vehicles}.tasks`,
      routerProps: {
        path: vehiclesRoutes.tasks,
        element: <VehicleDetail />,
      },
      requiredPermission: permissions.readTask,
    },
    {
      id: `${moduleKeys.vehicles}-customNotices`,
      requiredFeatureFlag: featureFlags.CUSTOM_NOTICES,
      routerProps: {
        path: vehiclesRoutes.customNotices,
        element: <VehicleDetail />,
      },
    },
    {
      id: `${moduleKeys.vehicles}-finances`,
      routerProps: {
        path: vehiclesRoutes.finances,
        element: <VehicleDetail />,
      },
    },
    {
      id: `${moduleKeys.vehicles}-finances-prices`,
      routerProps: {
        path: vehiclesRoutes.financesPrices,
        element: <VehicleDetail />,
      },
    },
    {
      id: `${moduleKeys.vehicles}-finances-earnings-costs`,
      routerProps: {
        path: vehiclesRoutes.financesEarningsCosts,
        element: <VehicleDetail />,
      },
    },
    {
      id: `${moduleKeys.vehicles}-finances-financing-leasing`,
      requiredFeatureFlag: featureFlags.VEHICLE_FINANCING,
      routerProps: {
        path: vehiclesRoutes.financesFinancingLeasing,
        element: <VehicleDetail />,
      },
    },
    {
      id: `${moduleKeys.vehicles}-interests`,
      routerProps: {
        path: vehiclesRoutes.interests,
        element: <VehicleDetail />,
      },
    },
    {
      id: `${moduleKeys.vehicles}-customers`,
      routerProps: {
        path: vehiclesRoutes.customers,
        element: <VehicleDetail />,
      },
    },
    {
      id: `${moduleKeys.vehicles}-documents`,
      routerProps: {
        path: vehiclesRoutes.documents,
        element: <VehicleDetail />,
      },
    },
    {
      id: `${moduleKeys.vehicles}-reservations`,
      routerProps: {
        path: vehiclesRoutes.reservations,
        element: <VehicleDetail />,
      },
    },
    {
      id: `${moduleKeys.vehicles}-test-drives`,
      routerProps: {
        path: vehiclesRoutes.testDrives,
        element: <VehicleDetail />,
      },
    },
    {
      id: `${moduleKeys.vehicles}-rental-orders`,
      requiredPermission: permissions.readRentalOrder,
      routerProps: {
        path: vehiclesRoutes.rentalOrders,
        element: <VehicleDetail />,
      },
    },
    {
      id: `${moduleKeys.vehicles}-caris-service-requests`,
      requiredPermission: permissions.readCarisServiceRequests,
      routerProps: {
        path: vehiclesRoutes.carisServiceRequests,
        element: <VehicleDetail />,
      },
    },
    {
      id: `${moduleKeys.vehicles}-postponed-jobs`,
      routerProps: {
        path: vehiclesRoutes.postponedJobs,
        element: <VehicleDetail />,
      },
    },
    {
      id: `${moduleKeys.vehicles}-ownership`,
      routerProps: {
        path: vehiclesRoutes.ownership,
        element: <VehicleDetail />,
      },
    },
    {
      id: `${moduleKeys.vehicles}-active-service-cases`,
      routerProps: {
        path: vehiclesRoutes.activeServiceCases,
        element: <VehicleDetail />,
      },
    },
    {
      id: `${moduleKeys.vehicles}-history-of-repairs`,
      routerProps: {
        path: vehiclesRoutes.historyOfRepairs,
        element: <VehicleDetail />,
      },
    },
    {
      id: `${moduleKeys.vehicles}-warehouse-info`,
      routerProps: {
        path: vehiclesRoutes.vehicleWarehouseInfo,
        element: <VehicleDetail />,
      },
    },
    {
      id: 'vehicles.threeHundredAndSixtyPhotos',
      routerProps: {
        path: vehiclesRoutes.threeHundredAndSixtyPhotos,
        element: <VehicleDetail />,
      },
    },
    {
      id: `${moduleKeys.vehicles}-activity-logs`,
      routerProps: {
        path: vehiclesRoutes.activityLog,
        element: <VehicleDetail />,
      },
    },
    {
      id: `${moduleKeys.vehicles}-email-activity-logs`,
      routerProps: {
        path: vehiclesRoutes.emailActivityLogs,
        element: <VehicleDetail />,
      },
    },
    {
      id: `${moduleKeys.vehicles}-serviceCampaigns`,
      routerProps: {
        path: vehiclesRoutes.serviceCampaigns,
        element: <VehicleDetail />,
      },
    },
    {
      id: `${moduleKeys.vehicles}-tires`,
      routerProps: {
        path: vehiclesRoutes.tires,
        element: <VehicleDetail />,
      },
    },
    {
      id: `${moduleKeys.vehicles}-checklists`,
      routerProps: {
        path: vehiclesRoutes.checklists,
        element: <VehicleDetail />,
      },
    },
  ],
};
